import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { FormField, ErrorMode, MarketingChannel, MarketingContext, CustomerProfile } from '@/models';
import { makeFormField, enforceMaxLength, isValidEmail, isValidPhoneNumber, stripNonNumerics, formatPhoneNumber, isValidCustomerName, isValidBirthday, stripLeadingOnesAndZeros, formatDate, stripLeadingZeros, isValidUkPostalCode, isValidUkBirthday, reverseFormatUKPhoneNumber, formatBirthdate, calculateAge, isSafariBrowser } from '@/helpers';
import moment from 'moment';
import { CustomerError, CustomerErrorCode } from '@/services/customer/customer.service';
import { AccountManagementEvent, AccountPageLoadEvent, ErrorEvent } from '@/services/analytics/events';
import { isAutoSubscribesToLoyaltyEmail, isAutoSubscribesToMarketingEmail } from '@/helpers/country';
import { getI18nMultiLineContent } from '@/i18n';
import { CountryCode } from '@/store/ui';
import { AUTOMATIC_EMAIL_MARKETING_AGE, AUTOMATIC_PERSONALIZATION_AGE, countries, ui } from '@/constants';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import useAccountPage from '@/composables/useAccountPage';

interface EditAccountForm {
  [key: string]: FormField;
  firstName: FormField;
  lastName: FormField;
  telephone: FormField;
  email: FormField;
  birthday: FormField;
  postalCode: FormField;
}

const EDIT_ACCOUNT_FORM: EditAccountForm = {
  firstName: makeFormField(),
  lastName: makeFormField(),
  telephone: makeFormField(),
  email: makeFormField(),
  country: makeFormField(),
  birthday: makeFormField(),
  postalCode: makeFormField()
};

@Component({
  name: 'CmgEditAccountModal',
})
export default class CmgEditAccountModal extends Vue {
  editAccountForm = EDIT_ACCOUNT_FORM;
  isLoading = false;
  isFormValid = true;
  formSubmissionErrorMessage = '';
  countrySwitchMessageLines: string[] = [];
  birthdayChangeModalShownOnce = false;
  phoneInvalid = false;

  async activated() {
    // Called when component is activated (e.g., shown in <keep-alive>)
    useAccountPage({ isModal: true }, this.$store, this.$services.customer, { router: this.$router });
  }

  @Watch('visible', { immediate: true, deep: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      await this.$services.customer.waitForCustomerInitialization();

      this.initializeForm();
    }
  }

  get legalCopy():string {
    return this.$services.config.getLegalText().updateProfile;
  }

  // On blur, the telephone number gets formatted, so
  // it requires max length of 14. When user is typing,
  // we want maxLength to be 11.
  get phoneInputMaxLength(): number {
    return this.form.telephone.formatted ? 14 : 11;
  }

  get customerProfile() {
    return this.$appStore.state.customer.profile;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isCountryUK(): boolean {
    return this.countryCode === CountryCode.GB;
  }

  get form(): EditAccountForm {
    return this.editAccountForm;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.editAccountModal.visible;
  }

  get countrySwitchMessagePath(): string {
    const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customerProfile);
    const willAutoEnrollInLoyalty = isAutoSubscribesToLoyaltyEmail(this.form.country.value);

    let messagePath = 'modals.editAccount.countrySwitchMessage.' +
      (willAutoEnrollInLoyalty ? 'autoEnroll.' : 'nonAutoEnroll.') +
      (isEnrolledInLoyalty ? 'forLoyalty' : 'forNonLoyalty');

    return messagePath;
  }

  get isNorthAmericaCountry(): boolean {
    return this.countryCode === CountryCode.US || this.countryCode === CountryCode.CA;
  }

  get isCountryChanged(): boolean {
    return this.customerProfile?.country !== this.form.country.value;
  }

  get telephonePlaceholder() {
    return this.isCountryUK ? 'UK Mobile Number' : 'Mobile Number';
  }

  get phoneNumberText() {
    return this.isCountryUK
      ? 'Account creation is only for customers who have a valid UK mobile number that can accept SMS messages. ' +
      'Your mobile number is required to complete two-step verification to enhance the security of your account.'
      : '';
  }

  get birthdayPlaceholder(): string {
    let placeholder = 'Birthday MM/DD/YYYY';
    if (this.isNorthAmericaCountry) {
      placeholder = this.isBirthdayEnabled ? 'Birthday MM/DD/YYYY' : 'Birthday MM/DD';
    }
    if (this.isCountryUK) {
      placeholder = 'Birthday DD/MM';
    }
    return placeholder;
  }

  get birthdayAriaLabel(): string {
    return this.isCountryUK
      ? 'Enter birthday in DD/MM format so we know when to celebrate.'
      : 'Enter birthday in MM/DD format so we know when to celebrate.';

  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get isTabletOrSmaller(): boolean {
    const width = this.$appStore.state.ui.clientWidth;
    return width <= ui.breakpoints.tabletMax;
  }

  get dateFormat(): string {
    return !this.isCountryUK ? 'MMDDYYYY' : 'DDMM';
  }

  get showEmailMarketingOptions(): boolean {
    return this.form.country.value === CountryCode.US;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isBirthdayEnabled(): boolean {
    const { userVerifiedBirthDate, externallyConfirmed18, dateOfBirth } = this.customer || {};
    return this.isCountryUK || !(userVerifiedBirthDate || (externallyConfirmed18 && dateOfBirth));
  }

  get birthday(): string {
    let bday = this.form.birthday.value ? `${formatBirthdate(this.form.birthday.value, this.form.country.value, '-', true)}` : '';
    bday = bday && !this.isNorthAmericaCountry ? moment(`${bday}-1972`, 'DD-MM-YYYY').format('MM-DD-YYYY') : bday;

    return bday;
  }

  showCountrySwitchRadioButton(country: string): boolean {
    if (this.isNorthAmericaCountry &&
      !this.customerProfile?.externallyConfirmed18 &&
      this.customerProfile?.userVerifiedBirthDate) {
      const age = calculateAge(this.customerProfile.dateOfBirth!);
      if (age < countries[country.toLowerCase()].minimumAgeOfEligibility) {
        return false;
      }
    }
    return true;
  }

  generateCountrySwitchMessageLines(): void {
    if (!this.customerProfile || !this.isCountryChanged) {
      this.countrySwitchMessageLines = [];
      return;
    }
    const fromCountryCode = this.customerProfile.country.toLocaleUpperCase();
    const toCountryCode = this.form.country.value.toLocaleUpperCase();

    const countryFrom = this.$t(`countries.${fromCountryCode}.shortName`);
    const countryTo = this.$t(`countries.${toCountryCode}.shortName`);

    const countryFromWithPrefixLastWord = this.$t(`countries.${fromCountryCode}.withPrefixShortNameLastWord`);
    const countryToWithPrefixLastWord = this.$t(`countries.${toCountryCode}.withPrefixShortNameLastWord`);

    this.countrySwitchMessageLines = getI18nMultiLineContent(this.countrySwitchMessagePath, { countryFrom, countryTo, countryFromWithPrefixLastWord, countryToWithPrefixLastWord });
  }

  validateFirstName(): void {
    if (this.form.firstName.value.length == 0) {
      this.form.firstName.error = 'First Name Required';
      this.form.firstName.errorMode = ErrorMode.PlaceHolder;
    } else if (!isValidCustomerName(this.form.firstName.value)) {
      this.form.firstName.error = 'No special characters allowed';
    } else {
      this.form.firstName.error = null;
    }
  }

  validateLastName(): void {
    if (this.form.lastName.value.length == 0) {
      this.form.lastName.error = 'Last Name Required';
    } else if (!isValidCustomerName(this.form.lastName.value)) {
      this.form.lastName.error = 'No special characters allowed';
    } else {
      this.form.lastName.error = null;
    }

    this.form.lastName.errorMode = ErrorMode.PlaceHolder;
  }

  validateEmail(): void {
    if (this.form.email.value.length == 0) {
      this.form.email.error = 'Email Required';
    } else {
      this.form.email.error = !isValidEmail(this.form.email.value) ? 'Please Enter A Valid Email' : '';

      if (this.form.email.value.includes('+') && this.countryCode !== CountryCode.GB) {
        let invalidEmailMessage = 'Please enter a valid email. ';
        let invalidEmailPlusMessage = 'Email cannot contain the \'+\' symbol.';
        let pleaseTryAgain = 'Please enter a valid email and try again.';
        if (this.isMobile) {
          this.form.email.error = invalidEmailPlusMessage;
        } else {
          this.form.email.error = invalidEmailPlusMessage + ' ' + pleaseTryAgain;
        }
      }
    }

    this.form.email.errorMode = ErrorMode.PlaceHolder;
  }

  validateTelephoneNumber(): void {
    this.phoneInvalid = false;
    if (this.form.telephone.value.length == 0) {
      this.form.telephone.error = 'Mobile Number Required';
    } else {

      if(this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase() == 'uk' || this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase() == 'gb') {
        if(this.form.telephone.value.length < 10) { 
          this.form.telephone.error = 'Phone Number must contain 10 digits';
        }
        else if(this.form.telephone.value.length > 10) {
          this.form.telephone.error = 'Phone Number must contain no more than 10 digits';
        }
        else {
          this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value, this.countryCode) ? 'Please Enter A Valid Phone Number' : '';
        }
      } else {
        this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value, this.countryCode) ? 'Please Enter A Valid Phone Number' : '';
      }
    }

    this.form.telephone.errorMode = ErrorMode.PlaceHolder;
  }

  sanitizePhoneNumber(value: string): string {
    return this.isCountryUK ? stripNonNumerics(value) : stripLeadingZeros(stripNonNumerics(value));
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value, this.countryCode);
  }

  validateUKPostalCode(): void {
    if (this.form.postalCode.value.length == 0) {
      this.form.postalCode.error = 'Postcode Required';
    } else {
      this.form.postalCode.error = !isValidUkPostalCode(this.formatPostalCode(this.form.postalCode.value)) ? 'Please Enter A Valid Postcode' : '';
    }

    this.form.postalCode.errorMode = ErrorMode.PlaceHolder;
  }

  formatPostalCode(value: string): string {
    return value?.toUpperCase();
  }

  formatBirthday(value: string): string {
    return formatBirthdate(value, this.countryCode, "/", this.isBirthdayEnabled);
  }

  validateCountry(): void {
    if (!this.form.country.value) {
      this.form.country.error = 'Please select a country';
    } else {
      this.form.country.error = '';
    }
  }

  validateBirthday(): void {
    // Validating birthday for different formats
    const isValid = !this.isCountryUK ? isValidBirthday(this.form.birthday.value) : isValidUkBirthday(this.form.birthday.value);
    this.form.birthday.error = !isValid
      ? `Please Enter A Valid Birthday ${this.dateFormat}`
      : '';

    this.form.birthday.errorMode = ErrorMode.PlaceHolder;
  }

  enforceMaxLength(value: string, formField: FormField): string {
    const validLengths = this.isCountryUK ? [4] : [8];
    return enforceMaxLength(value, validLengths, formField);
  }

  onSelectCountry(value: string): void {
    this.form.country.error = '';
    this.form.country.dirty = true;
    this.form.country.value = value;
    this.generateCountrySwitchMessageLines();
    if(value === CountryCode.US || value === CountryCode.CA) {
      const formattedRegion = `en-${value.toLowerCase()}`;
      this.$services.config.setLegalText(formattedRegion);
    }

    this.$services.analytics.send(new AccountPageLoadEvent('change-country'));
  }

  validateForm() {
    this.isFormValid = true;

    this.validateFirstName();
    this.validateLastName();
    this.validateEmail();
    this.validateTelephoneNumber();
    this.validateBirthday();
    this.validateCountry();

    if (this.isCountryUK) {
      this.validateUKPostalCode();
    }

    for (const key of Object.keys(this.form)) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }
  }

  initializeForm(): void {
    this.isFormValid = true;
    this.formSubmissionErrorMessage = '';
    if (!this.customerProfile) {
      return;
    }

    this.form.firstName.value = this.customerProfile.firstName;
    this.form.lastName.value = this.customerProfile.lastName;
    this.form.email.value = this.customerProfile.email;

    const phoneNumber = this.countryCode !== CountryCode.GB
      ? this.customerProfile.phoneNumber
      : reverseFormatUKPhoneNumber(this.customerProfile.phoneNumber);

    this.form.telephone.value = phoneNumber;
    this.form.telephone.formatted = this.formatPhoneNumber(phoneNumber);

    this.form.country.value = this.customerProfile.country;
    this.form.birthday.value = !!this.customerProfile.dateOfBirth ? moment(this.customerProfile.dateOfBirth).format(this.dateFormat) : '';
    this.form.birthday.formatted = !!this.customerProfile.dateOfBirth ? this.formatBirthday(moment(this.customerProfile.dateOfBirth).format(this.dateFormat)) : '';

    this.form.postalCode.value = this.customerProfile.zip || '';
  }
  async onUpdateAccount(): Promise<void> {
    await this.$services.config.setLegalText();
    this.isLoading = true;
    this.formSubmissionErrorMessage = '';
    this.phoneInvalid = false;

    this.validateForm();

    if (!this.isFormValid || !this.customerProfile) {
      this.isLoading = false;
      return;
    }

    const birthday = this.form.birthday.value;

    const oldEmail = this.customerProfile.email;
    const newEmail = this.form.email.value;
    const changingEmail = oldEmail != newEmail;

    const hasEmailSubscription = this.$services.customer.hasSubscription(MarketingChannel.Email, MarketingContext.Marketing);
    const hasSmsSubscription = this.$services.customer.hasSubscription(MarketingChannel.Sms, MarketingContext.Marketing);
    const enrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customerProfile);

    const oldPhoneNumber = this.customerProfile.phoneNumber;
    const newPhoneNumber = this.form.telephone.value;
    const changingPhoneNumber = oldPhoneNumber != newPhoneNumber;

    const oldCountry = this.customerProfile.country;
    const newCountry = this.form.country.value;
    const shouldAutoSubscribeToMarketingEmail = isAutoSubscribesToMarketingEmail(newCountry);
    const shouldAutoSubscribeToLoyaltyEmail = isAutoSubscribesToLoyaltyEmail(newCountry);
    const changingCountry = oldCountry !== newCountry;

    const oldBirthday = !!this.customerProfile.dateOfBirth ? moment(this.customerProfile.dateOfBirth).format(this.dateFormat) : '';
    const changingBirthday = oldBirthday !== birthday;

    if (changingBirthday && this.underageCustomer && !this.birthdayChangeModalShownOnce) {
      this.isLoading = false
      this.birthdayChangeModalShownOnce = true;
      this.$services.ui.setModalVisibility('basicError', true, {
        header: `Confirm Your Birthday`,
        content: 'Please double check your birthday,<br/> you won\'t be able to change it.',
        isShort: true,
        options: [
          {
            text: 'Go Back',
            type: ButtonType.Primary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
            },
          },
        ],
      });
    } else {

      const sendUIDataToGigya = (enrolledInLoyalty && (changingEmail || changingCountry));

      // All the unsubscribing goes on here
      let needToRefresh = false;
      try {
        if (hasSmsSubscription && changingPhoneNumber) {
          await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
          needToRefresh = true;
        }
        if (hasEmailSubscription && (changingEmail || changingCountry)) {
          await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
          needToRefresh = true;
        }
        if (enrolledInLoyalty && (changingEmail || changingCountry)) {
          await this.$services.loyalty.unenrollInLoyalty();
          needToRefresh = true;
        }
        
        if (needToRefresh) {
          // Refreshing after unsubscribing seems allow us to cleanly subscribe again later.
          this.$services.customer.setProfileSubscriptionChangeOnly(true);
          await this.$services.customer.refreshCustomerProfileInfo(true, false, true);
        }
      } catch (err) {
        this.$services.logger.error('Failed to unsubscribe before updating customer.');
      }
      // Updating customer
      const customerPartial: any = {
        firstName: this.form.firstName.value,
        lastName: this.form.lastName.value,
        email: newEmail,
        phoneNumber: this.form.country.value === CountryCode.GB ? `44${stripLeadingZeros(this.form.telephone.value)}` : this.form.telephone.value,
        country: this.form.country.value,
        dateOfBirth: this.birthday,
        zip: this.formatPostalCode(this.form.postalCode.value),
      }

      if (this.underageCustomer) {
        customerPartial.deactivate = true;
      }

      if (sendUIDataToGigya) {
        customerPartial.hasSeenRewardsGenOnboarding = false;
        customerPartial.hasSeenRewardsExchOnboarding = false;
      }

      try {
        await this.$services.customer.updateCustomer(customerPartial);
      } catch (err: any) {
        this.$services.logger.error('failed to update customer');
        this.formSubmissionErrorMessage = err instanceof CustomerError ? err.message : 'Error trying to update customer';
        this.$services.analytics.send(new ErrorEvent('account', this.formSubmissionErrorMessage, 'edit-account'));
        if (err.errorCode === CustomerErrorCode.UpdateAccountEmailInUse) {
          this.$services.ui.setModalVisibility('verifyMobileNumber', false);
        } else if (err.errorCode === CustomerErrorCode.EmailUpdateRequiresTwoStepVerification || err.errorCode === CustomerErrorCode.PhoneNumberUpdateRequiresTwoStepVerification) {
          this.$services.ui.setModalVisibility('editAccountModal', false);
          this.$services.ui.setModalVisibility('verifyMobileNumber', true, {
            email: this.$appStore.state.customer.profile?.email ?? '',
            phoneNumber: newPhoneNumber ?? '',
            oldPhoneNumber: "",
            requiresPasswordVerification: err.errorCode === CustomerErrorCode.PhoneNumberUpdateRequiresTwoStepVerification,
            requiresSignOutOnFailure: false,
            onSuccess: async () => {
              try {
                await this.$services.customer.updateCustomer(customerPartial);
              } catch (err: any) {
                this.$services.logger.error('failed to update customer');
                if (err.errorCode === CustomerErrorCode.UserUpdateAgeIneligible) {
                  await this.signOut();
                }
              }

              if (changingPhoneNumber || changingEmail) {
                this.$services.ui.setModalVisibility('clearWalletModal', true);
              }

              await this.proceedAfterAccountUpdate(changingPhoneNumber, changingCountry, changingEmail, changingBirthday,
                hasSmsSubscription, hasEmailSubscription, enrolledInLoyalty, shouldAutoSubscribeToMarketingEmail,
                shouldAutoSubscribeToLoyaltyEmail);
            },
            onFailure: async () => {
              if (sendUIDataToGigya) {
                await this.$services.customer.updateCustomer({ hasSeenRewardsGenOnboarding: false, hasSeenRewardsExchOnboarding: false });
              }
              await this.signOut();
            },
            onCancel: async () => {
              if (sendUIDataToGigya) {
                await this.$services.customer.updateCustomer({ hasSeenRewardsGenOnboarding: false, hasSeenRewardsExchOnboarding: false });
              }
              this.$services.ui.setModalVisibility('verifyMobileNumber', false);
              this.$services.ui.setModalVisibility('editAccountModal', true);
            },
          });
          Vue.nextTick(() => {
            this.$services.ui.focusDiv('.cmg-verify-mobile-number-modal');
          });
        } else if (err.errorCode === CustomerErrorCode.UserUpdateAgeIneligible) {
          await this.signOut();
        } else if(err.errorCode === CustomerErrorCode.UpdatePhoneInUse) {
          this.phoneInvalid = true;
          let desktopWebErrorMessage = 'Mobile number has already been used on two accounts. Please enter a valid number.';
          let mobileWebErrorMessage = 'Number already in use. Try another?';
          this.$services.ui.setModalVisibility('verifyMobileNumber', false);
          this.form.telephone.error = this.$services.ui.isMobile() ? mobileWebErrorMessage : desktopWebErrorMessage;
          this.formSubmissionErrorMessage =  '';
        }

        this.isLoading = false;
        return;
      }

      await this.proceedAfterAccountUpdate(changingPhoneNumber, changingCountry, changingEmail, changingBirthday,
        hasSmsSubscription, hasEmailSubscription, enrolledInLoyalty, shouldAutoSubscribeToMarketingEmail,
        shouldAutoSubscribeToLoyaltyEmail);
    }
  }

  async proceedAfterAccountUpdate(changingPhoneNumber: boolean, changingCountry: boolean, changingEmail: boolean, changingBirthday: boolean,
    hasSmsSubscription: boolean, hasEmailSubscription: boolean, enrolledInLoyalty: boolean,
    shouldAutoSubscribeToMarketingEmail: boolean, shouldAutoSubscribeToLoyaltyEmail: boolean
  ): Promise<void> {
    const date = !isSafariBrowser()
      ? new Date(formatBirthdate(this.form.birthday.value, this.form.country.value, '-', true)) // send true to calc age with year
      : new Date(moment(this.form.birthday.value, 'MM-DD-YYYY').format('YYYY-MM-DD'));
    const age = calculateAge(date);

    // Subscribing where necessary
    try {
      if (hasSmsSubscription && (changingPhoneNumber || changingCountry)) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      }
      if (changingCountry) {
        if (age < AUTOMATIC_EMAIL_MARKETING_AGE) {
          if (hasEmailSubscription) {
            await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
          }
        } else {
          if (shouldAutoSubscribeToMarketingEmail) {
            await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
          }
        }
      }
      if ((enrolledInLoyalty && changingEmail && !changingCountry) || (changingCountry && shouldAutoSubscribeToLoyaltyEmail)) {
        await this.$services.loyalty.enrollInLoyalty({
          customer: this.customerProfile,
          pageTitle: document.title,
          shouldSubscribeToMarketingEmail: false,
        });
      }
      this.$services.customer.setProfileSubscriptionChangeOnly(true);
      await this.$services.customer.refreshCustomerProfileInfo(true, false, true);
    } catch (err) {
      this.$services.logger.error('Could not subscribe after updating customer');
    }

    if (changingBirthday) {
      if (age < AUTOMATIC_PERSONALIZATION_AGE) {
        this.showPersonalizationOptionsModal();
      } else {
        if (!this.$appStore.state.customer.optedIntoPersonalizationMessaging) {
          await this.$services.customer.updateCustomer({
            optedIntoPersonalization: true
          });
        }
      }
    }
    this.isLoading = false;
    this.onClose();
    this.$services.analytics.send(new AccountManagementEvent('personal-preferences|edit-account'));
  }

  async showPersonalizationOptionsModal(): Promise<void> {
    this.$services.ui.setModalVisibility('personalizedContent', true, {
      showEmailMarketingOptions: this.showEmailMarketingOptions,
      onSuccess: async () => {
        this.isLoading = true;
        await this.$services.customer.updateCustomer({
          optedIntoPersonalization: this.$appStore.state.customer.optedIntoPersonalizationMessaging,
        });

        if (this.showEmailMarketingOptions) {
          if (this.$appStore.state.customer.optedIntoEmailMarketingFromOptions) {
            await this.$services.customer.subscribeToMarketingChannel(
              MarketingChannel.Email,
              MarketingContext.Marketing
            );
          } else {
            await this.$services.customer.unsubscribeToMarketingChannel(
              MarketingChannel.Email,
              MarketingContext.Marketing
            );
          }
        }
        this.$services.customer.setProfileSubscriptionChangeOnly(true);
        await this.$services.customer.refreshCustomerProfileInfo(true, false, true);
        this.isLoading = false;
      }
    });
  }

  get infoIconImageUrl(): string {
    return this.$appStore.state.cms.images.icons.infoIcon.default;
  }

  get underageCustomer(): boolean {
    const birthday = this.form.birthday.value;
    const date = !isSafariBrowser()
      ? new Date(formatBirthdate(birthday, this.form.country.value, '-', this.isBirthdayEnabled))
      : new Date(moment(birthday, 'MM-DD-YYYY').format('YYYY-MM-DD'));
    const age = calculateAge(date);
    if (age < countries[this.form.country.value.toLowerCase()].minimumAgeOfEligibility) {
      return true;
    }
    return false;
  }

  onClickInfoIcon() {
    const isUS = this.form.country.value == CountryCode.US
    const getUrl = (path: string) => isUS ? `https://www.chipotle.com/${path}` : `https://www.chipotle.ca/${path}`;
    const modalContent = `Depending on your age, we need to ask for your permission to serve you personalized content and offers.
                          Plus, if you're a Rewards Member, we'll celebrate with you! <br/> <br/> 
                          Check out our <b><a href=${getUrl('privacy-policy')} target="_blank" tabindex="0">Privacy Policy</a > </b>,
                           <b><a href=${getUrl('rewards-terms')} target="_blank" tabindex="0">Rewards Terms</a > </b>, 
                          and <b><a href=${getUrl('terms-of-use')} target="_blank" tabindex="0">Terms of Use</a > </b> to learn more.<br/> <br/>`;
    const header = !this.isBirthdayEnabled
      ? 'Why can\'t I update my birthday?'
      : 'Why do we ask for your birthday ?';
    this.$services.ui.setModalVisibility('basicError', true, {
      header,
      content: modalContent,
      options: [
        {
          text: 'Ok',
          onClick: () => this.$services.ui.setModalVisibility('basicError', false),
        },
      ],
    });
  }

  onClose() {
    this.initializeForm();
    this.form.email.error = '';
    this.$services.ui.setModalVisibility('editAccountModal', false);
    this.$services.ui.focusBack();
  }

  async signOut() {
    return this.$services.customer.signOut().then(() => {
    }).finally(async () => {
      // Clear out the stored JWT in the common iframe
      const iframe = (document.getElementById('sessionFrame') as unknown) as HTMLIFrameElement;
      if (iframe) {
        const win = iframe.contentWindow;
        if (win) {
          win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'remove' }), this.$services.config.getSessionFrameUrl());
        }
      }
      window.location.href = '/';
    });
  }
}
